import { HIRING_RELIABILITY_CONFIG } from "@clipboard-health/contract-backend-main";
import { startOfDay as cbhStartOfDay } from "@clipboard-health/date-time";
import {
  addDays,
  differenceInMinutes,
  getHours,
  isAfter,
  isBefore,
  isToday,
  setHours,
  setMinutes,
  startOfDay,
} from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

export function isInterviewLateCancellation({
  interviewStart,
  interviewCreatedAt,
  isSchedulingPastWorkplaceStartHour,
}: {
  interviewStart: Date;
  interviewCreatedAt: string;
  isSchedulingPastWorkplaceStartHour: boolean;
}): boolean {
  const graceWindow = HIRING_RELIABILITY_CONFIG.GRACE_WINDOW_IN_MINUTES;
  const isWithinLateCancelGraceWindow =
    differenceInMinutes(Date.now(), new Date(interviewCreatedAt).getTime()) < graceWindow;

  if (isWithinLateCancelGraceWindow) {
    return false;
  }

  return isAfter(new Date(), interviewStart) || isSchedulingPastWorkplaceStartHour;
}

export function getInterviewAvailability({
  interviewAvailability,
  workerTimezone,
  workplaceTimezone,
  isRescheduleForToday,
}: {
  interviewAvailability: Array<{
    start: Date;
    end: Date;
  }>;
  workerTimezone: string;
  workplaceTimezone: string;
  isRescheduleForToday: boolean;
}) {
  if (isRescheduleForToday) {
    return interviewAvailability.filter((timeslot) => {
      const { start, end } = timeslot;
      const workplaceStart = utcToZonedTime(
        zonedTimeToUtc(start, workerTimezone),
        workplaceTimezone
      );
      const workplaceEnd = utcToZonedTime(zonedTimeToUtc(end, workerTimezone), workplaceTimezone);
      return isToday(workplaceStart) && isToday(workplaceEnd);
    });
  }

  return interviewAvailability;
}

export function getWorkplaceStartHour({
  interviewStart,
  workplaceTimezone,
  workerTimezone,
  startHour,
}: {
  interviewStart?: Date | undefined;
  workplaceTimezone: string;
  workerTimezone: string;
  startHour: number;
}) {
  return utcToZonedTime(
    zonedTimeToUtc(
      setMinutes(
        setHours(utcToZonedTime(interviewStart ?? new Date(), workplaceTimezone), startHour),
        0
      ),
      workplaceTimezone
    ),
    workerTimezone
  );
}

/**
 * Calculates the document upload deadline for a pending interview.
 * The deadline is 4 PM the day before the interview in the worker's timezone.
 *
 * @param interviewStart - The interview start time as a UTC Date
 * @param workerTimezone - The worker's IANA timezone string
 * @returns The document deadline as a UTC Date
 */
export function getDocumentUploadDeadline({
  interviewStart,
  workerTimezone,
}: {
  interviewStart: Date;
  workerTimezone: string;
}): Date {
  const tzOptions = { timeZone: workerTimezone };
  const deadlineUtc = new Date(
    cbhStartOfDay(interviewStart, tzOptions).getTime() - 8 * 60 * 60 * 1000
  );
  return deadlineUtc;
}

/**
 * Filters interview availability for the "submit documents later" flow.
 * Workers need time to upload documents before their interview.
 *
 * Rules:
 * - Never show same-day interview slots
 * - If it's after 2 PM, don't show next-day interview slots either
 *   (not enough turnaround time for document upload)
 */
export function filterAvailabilityForSubmitDocumentsLater({
  interviewAvailability,
  workerTimezone,
}: {
  interviewAvailability: Array<{
    start: Date;
    end: Date;
  }>;
  workerTimezone: string;
}) {
  const now = new Date();
  const nowInWorkerTimezone = utcToZonedTime(now, workerTimezone);
  const currentHour = getHours(nowInWorkerTimezone);
  const todayInWorkerTimezone = startOfDay(nowInWorkerTimezone);

  // If it's after 2 PM, the earliest available date is 2 days from now
  // Otherwise, the earliest available date is tomorrow
  const isAfter2pm = currentHour >= 14;
  const daysToAdd = isAfter2pm ? 2 : 1;
  const earliestAvailableDate = startOfDay(addDays(todayInWorkerTimezone, daysToAdd));

  return interviewAvailability.filter((timeslot) => {
    const slotStartInWorkerTimezone = timeslot.start;
    const slotDateInWorkerTimezone = startOfDay(slotStartInWorkerTimezone);

    // Only include slots on or after the earliest available date
    return !isBefore(slotDateInWorkerTimezone, earliestAvailableDate);
  });
}
